import { Box, Fab, List, useMediaQuery, useTheme } from '@mui/material';
import { FETCH_ORG_CSV_DATA, FETCH_ORG_LIST } from '../../../data/organization/action-types';
import { INIT_ORGANIZATION, getOrgStatusMenu, orgCSVHeaderData } from '../../../utils/constants';
import React, { useState } from 'react';

import APP_NAV from '../../../routes/app-nav';
import AddIcon from '../../../components/CustomIcons/AddIcon';
import CircularProgressBar from '../../../components/CircularProgressBar';
import CustomButton from '../../../components/CustomButton';
import InlineAlert from '../../../components/InlineAlert';
import MenuData from '../../../types/ui/menu-data';
import NoDataFound from '../../../components/NoDataFound';
import OrgListSearchFilterHeader from './OrgListSearchFilterHeader';
import Organization from '../../../types/organization';
import OrganizationListItem from './OrganizationListItem';
import Util from '../../../utils/util';
import saveAs from 'file-saver';
import { useNavigate } from 'react-router-dom';
import { useOrganizationApi } from '../../../data/organization/api';
import useStyles from './styles';
import { useTranslation } from 'react-i18next';

/**
 * Renders the Organization List screen, allowing users to view, search, and filter organizations.
 *
 * @returns {JSX.Element} The Organization List screen component.
 */
const OrganizationListScreen: React.FC = () => {

  const styles = useStyles();
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('md'));
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchKey, setSearchKey] = useState<string>('');
  const [orgStatusFilter, setOrgStatusFilter] = useState<Array<MenuData>>(getOrgStatusMenu());
  const orgApi = useOrganizationApi();
  const apiStatus = orgApi.state.orgState.apiStatus;

  /**
   * Searches and filters organizations based on search key and selected statuses.
   *
   * @returns {Organization[]} An array of filtered organizations.
   */
  const searchAndFilterSites = () => {
    const checkedStatuses = orgStatusFilter
      .filter((menu: MenuData) => menu.isChecked)
      .map((menu: MenuData) => menu.id);

    return orgApi.state.orgState.orgList.filter((org: Organization) =>
      org.details.name.toLowerCase().includes(searchKey.toLowerCase()) &&
      checkedStatuses.includes(org.details.status)
    );
  };

  const filteredOrgList = searchAndFilterSites();

  /**
   * Navigates to the organization detail screen for the selected organization.
   *
   * @param {Organization} item The selected organization.
   */
  const onItemSelected = (item: Organization) => {
    if (item.details.id && !item.isFetched) {
      orgApi.fetchOrganizationDetail(item.details.id);
    }
    const navPath = isLargeScreen ? `${APP_NAV.ORGANIZATION}${APP_NAV.ORG_DETAIL_REL_PATH}`
      : APP_NAV.ORG_DETAIL_REL_PATH.replace('/', '');
    navigate(navPath, {
      state: {
        ...item
      }
    });
  };

  /**
   * Handles the click event for downloading the organization data as a CSV file.
   * It fetches the organization data using the orgApi, formats it into an XLSX blob,
   * and then triggers the download using the 'file-saver' library.
   */
  const onOrgCSVDownloadClick = () => {
    orgApi.fetchAllOrgCSVData().then(rows => {
      if (!Util.isArrayEmpty(rows)) {
        const featureList = orgApi.state.orgState.featureList.sort((a, b) =>
          a.label.toLowerCase().localeCompare(b.label.toLowerCase())
        ).map(item => item.label);
        const csvHeaderList = [...orgCSVHeaderData(), ...featureList];
        const xlsxBlob = Util.getXLSXBlob(csvHeaderList, rows as Array<Array<string>>);
        saveAs(xlsxBlob, 'Nucleus_All_Organisation_Data.xlsx');
      }
    });
  }

  return (
    <Box sx={styles.wrapper}>
      <Box sx={styles.innerWrapper}>
        <InlineAlert message={Util.getApiError([FETCH_ORG_LIST], apiStatus)} />
        <OrgListSearchFilterHeader
          orgStatusFilter={orgStatusFilter}
          onSearchChange={setSearchKey}
          onOrgStatusFilterChange={setOrgStatusFilter}
        />
        {Util.isArrayEmpty(filteredOrgList) ? (
          <NoDataFound message={searchKey ? t('noResultsFound') : undefined} />
        ) : (
          <List sx={styles.list}>
            {filteredOrgList.map((item: Organization, index: number) =>
              <OrganizationListItem
                key={`org_list_item_${index}`}
                organization={item.details}
                onItemSelected={() => onItemSelected(item)} />
            )}
          </List>
        )}
        <Box sx={styles.downloadBtn}>
          <CustomButton
            title={t('downloadReport')}
            color='secondary'
            fullWidth
            onClick={onOrgCSVDownloadClick}
            disabled={Util.isApiLoading([FETCH_ORG_CSV_DATA], apiStatus)}
          />
        </Box>
        <Fab color='primary' aria-label='add' sx={styles.fab} onClick={() => onItemSelected(INIT_ORGANIZATION)}>
          <AddIcon />
        </Fab>
      </Box>
      <CircularProgressBar show={Util.isApiLoading([FETCH_ORG_LIST], apiStatus)} />
    </Box>
  );
};

export default OrganizationListScreen;